// src/layouts/CustomAppBar.tsx
//
// v3 "3c hybrid" app bar: slim, warm off-white, hairline bottom border. Left:
// breadcrumb only (Group › Section) — the product name lives once, on the rail
// lockup. Right: environment pill (only outside production) and the language
// selector. The profile does NOT live here — it anchors the bottom of the rail
// (ProfileRailCard). Pattern A: on desktop the bar is absolute over the content
// column, starting at the rail's right edge and tracking its collapse.
import React from 'react';
import { AppBar, LocalesMenuButton, useSidebarState, useTranslate, useGetIdentity } from 'react-admin';
import { alpha, Box, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SwitchAccountRoundedIcon from '@mui/icons-material/SwitchAccountRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useLocation } from 'react-router-dom';
import { resourcesConfig } from '../config/resourceConfig';
import { MENU_GROUPS } from './CustomMenu';
import { getImpersonation, stopImpersonation } from '../utils/impersonation';

// Deployment environment. Set VITE_APP_ENV at build time; defaults: production
// builds -> production (no pill), dev server -> test.
const IS_PRODUCTION =
    (import.meta.env.VITE_APP_ENV ?? (import.meta.env.PROD ? 'production' : 'test')) === 'production';

/** Nested list routes → the grouped page that hosts them (for the breadcrumb). */
const NESTED_PARENT: Record<string, string> = {
    '/groups': 'admin-options',
    '/hotels': 'admin-options',
    '/tag-types': 'admin-options',
    '/locker-types': 'admin-options',
    '/locker-controllers': 'devices',
    '/hmis': 'devices',
    '/hmi-staff': 'devices',
};

/** Resolve the current section (resource label + rail group label) from the route. */
const findCurrentSection = (pathname: string): { groupLabelKey: string | null; nameKey: string } | null => {
    if (pathname === '/') {
        const group = MENU_GROUPS.find(g => g.members.includes('dashboard'));
        return { groupLabelKey: group?.labelKey ?? null, nameKey: 'ra.page.dashboard' };
    }
    const resource = Object.values(resourcesConfig).find((r: any) =>
        pathname === r.route || pathname.startsWith(`${r.route}/`)
    ) as any;
    if (!resource) return null;
    // Grouped-page children resolve their group through the hosting page.
    const parentName = NESTED_PARENT[resource.route] ?? resource.name;
    const group = MENU_GROUPS.find(g => g.members.includes(parentName));
    return {
        groupLabelKey: group?.labelKey ?? null,
        nameKey: resource.name === 'admin-options'
            ? 'resources.adminOptions.name'
            : resource.name === 'devices'
                ? 'resources.devices.name'
                : resource.label,
    };
};

export const CustomAppBar: React.FC = () => {
    const theme = useTheme();
    const translate = useTranslate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useSidebarState();
    const { data: identity } = useGetIdentity();
    // Developer has no rail (EmptyMenu/EmptySidebar in CustomLayout): the bar
    // spans the full width and hides the sidebar toggle.
    const isDeveloper = identity?.role === 'Developer';
    const impersonation = getImpersonation();

    const current = findCurrentSection(location.pathname);
    const railWidth = isDeveloper
        ? '0px'
        : (open ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth);

    const handleExitImpersonation = () => {
        stopImpersonation();
        // Effective role reverts to the real one: any privileged view already mounted is stale.
        window.location.hash = '#/';
        window.location.reload();
    };

    return (
        <AppBar
            userMenu={false}
            toolbar={
                <>
                    {/* Impersonation pill — persistent while active, visible on every screen. */}
                    {impersonation && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                height: '26px',
                                padding: '0 6px 0 10px',
                                borderRadius: theme.custom.radius.full,
                                backgroundColor: 'rgba(239,68,68,0.12)',
                                color: '#b91c1c',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <SwitchAccountRoundedIcon sx={{ fontSize: '16px' }} />
                            <Typography
                                component="span"
                                noWrap
                                sx={{
                                    fontSize: 'inherit',
                                    fontWeight: 'inherit',
                                    color: 'inherit',
                                    maxWidth: { xs: '110px', sm: '260px' },
                                }}
                            >
                                {translate('common.impersonation.pill', { name: impersonation.displayName })}
                            </Typography>
                            <Tooltip title={translate('common.impersonation.exit')}>
                                <IconButton
                                    size="small"
                                    onClick={handleExitImpersonation}
                                    sx={{ padding: '2px', color: 'inherit' }}
                                    aria-label={translate('common.impersonation.exit')}
                                >
                                    <CloseRoundedIcon sx={{ fontSize: '15px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Environment pill — only outside production. */}
                    {!IS_PRODUCTION && (
                        <Box
                            sx={{
                                display: { xs: 'none', sm: 'flex' },
                                alignItems: 'center',
                                gap: 0.75,
                                height: '26px',
                                padding: '0 10px',
                                borderRadius: theme.custom.radius.full,
                                backgroundColor: alpha(theme.palette.warning.main, 0.16),
                                color: theme.palette.warning.dark,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: theme.palette.warning.main,
                                }}
                            />
                            TEST ENV
                        </Box>
                    )}
                    <LocalesMenuButton />
                </>
            }
            elevation={0}
            sx={{
                backgroundColor: theme.palette.background.appbar,
                color: theme.palette.text.primary,
                height: isMobile ? '52px' : '44px',
                minHeight: isMobile ? '52px' : '44px',
                // Patrón A · escritorio: la barra va "encajada" SOBRE la columna
                // de contenido — absoluta, empezando en el borde derecho del rail
                // y llegando hasta el borde derecho del viewport. Móvil: fija a
                // todo el ancho sobre el rail overlay.
                position: isMobile ? 'fixed' : 'absolute',
                top: 0,
                left: isMobile ? 0 : railWidth,
                right: 'auto',
                width: isMobile ? '100%' : `calc(100% - ${railWidth})`,
                zIndex: isMobile ? 1200 : theme.zIndex.appBar,
                transition: 'left 0.2s ease-in-out, width 0.2s ease-in-out',
                borderBottom: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',

                '& .MuiToolbar-root': {
                    padding: { xs: '0 8px', sm: '0 16px' },
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: isMobile ? '52px' : '44px',
                    minHeight: isMobile ? '52px' : '44px !important',
                    backgroundColor: theme.palette.background.appbar,
                    border: 'none',
                },
                // Developer: hide sidebar toggle button (RA-injected)
                ...(isDeveloper && {
                    '& [class*="SidebarToggle"], & [class*="menuButton"]': {
                        display: 'none !important',
                    },
                }),
            }}
        >
            {/* Parte izquierda */}
            {!isMobile && (
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    minWidth: 0,
                    gap: 1.5,
                }}>
                    {!isDeveloper && (
                        <IconButton
                            color="inherit"
                            aria-label={translate('ra.action.menu', 'Menu')}
                            onClick={() => setOpen(!open)}
                            edge="start"
                            sx={{
                                mr: 2,
                                display: { md: 'none' },
                                width: '36px',
                                height: '36px',
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    {/* v3 "1a": app bar carries ONLY the breadcrumb (Group › Section),
                        no wordmark — the product name lives once, on the rail. */}
                    {current && (
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                                color: theme.custom.text.tertiary,
                                fontSize: '0.8125rem',
                            }}
                        >
                            {current.groupLabelKey && (
                                <>
                                    <Box component="span">{translate(current.groupLabelKey)}</Box>
                                    <ChevronRightRoundedIcon sx={{ fontSize: '16px' }} />
                                </>
                            )}
                            <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                {translate(current.nameKey, { smart_count: 2 })}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </AppBar>
    );
};

export default CustomAppBar;
